// File System Access API 유틸리티
// 브라우저에서 직접 .md (마크다운) 파일을 열고 저장할 수 있게 해줌

// 파일 핸들러 타입 정의
interface FileSystemFileHandle {
  getFile(): Promise<File>;
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream extends WritableStream {
  write(data: string): Promise<void>;
  close(): Promise<void>;
}

interface FilePickerOptions {
  types?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
  excludeAcceptAllOption?: boolean;
  multiple?: boolean;
}

interface FileSaveOptions {
  types?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
  suggestedName?: string;
}

declare global {
  interface Window {
    showOpenFilePicker?: (options?: FilePickerOptions) => Promise<FileSystemFileHandle[]>;
    showSaveFilePicker?: (options?: FileSaveOptions) => Promise<FileSystemFileHandle>;
  }
}

const fileOptions = {
  types: [
    {
      description: 'Markdown files',
      accept: {
        'text/markdown': ['.md']
      }
    }
  ]
};

// File System Access API가 지원되는지 확인
export const isFileSystemAccessSupported = (): boolean => {
  return 'showOpenFilePicker' in window && 'showSaveFilePicker' in window;
};

// .md 파일 열기
export const openTextFile = async (): Promise<{ content: string; name: string } | null> => {
  try {
    if (!isFileSystemAccessSupported()) {
      console.warn('File System Access API not supported');
      return await openTextFileViaInput();
    }

    const [fileHandle] = await window.showOpenFilePicker!({
      ...fileOptions,
      multiple: false
    });

    const file = await fileHandle.getFile();
    const content = await file.text();

    return {
      content,
      name: file.name.replace('.md', '') || 'Untitled'
    };
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      console.error('Error opening file:', error);
    }
    return null;
  }
};

// 대체 방법: input[type=file] 사용
const openTextFileViaInput = (): Promise<{ content: string; name: string } | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.md,text/markdown';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        resolve(null);
        return;
      }

      try {
        const content = await file.text();
        resolve({
          content,
          name: file.name.replace('.md', '') || 'Untitled'
        });
      } catch (error) {
        console.error('Error reading file:', error);
        resolve(null);
      }
    };

    input.oncancel = () => resolve(null);
    input.click();
  });
};

// .md 파일 저장
export const saveTextFile = async (content: string, filename?: string): Promise<boolean> => {
  try {
    if (!isFileSystemAccessSupported()) {
      return downloadTextFile(content, filename);
    }

    const fileHandle = await window.showSaveFilePicker!({
      ...fileOptions,
      suggestedName: `${filename || 'document'}.md`
    });

    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();

    return true;
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      console.error('Error saving file:', error);
    }
    return false;
  }
};

// 대체 방법: 다운로드 링크 생성
const downloadTextFile = (content: string, filename?: string): boolean => {
  try {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || 'document'}.md`;
    a.style.display = 'none';
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    return false;
  }
};

// 파일 내보내기 (다양한 형식)
export const exportDocument = (content: string, filename: string, format: 'txt' | 'md' = 'txt') => {
  const mimeTypes = {
    txt: 'text/plain',
    md: 'text/markdown'
  };

  const blob = new Blob([content], { type: mimeTypes[format] });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.${format}`;
  a.style.display = 'none';
  
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  
  URL.revokeObjectURL(url);
};

// 클립보드에서 텍스트 읽기
export const readFromClipboard = async (): Promise<string | null> => {
  try {
    if (!navigator.clipboard) return null;
    return await navigator.clipboard.readText();
  } catch (error) {
    console.error('Error reading from clipboard:', error);
    return null;
  }
};

// 클립보드에 텍스트 쓰기
export const writeToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Error writing to clipboard:', error);
    return false;
  }
};